// <s-marquee> — seamless continuous scroll of its [data-marquee-track] content.
// Clones the track once for a gapless loop and animates with the Web Animations API.
// Respects prefers-reduced-motion (no animation) and pauses on hover. One component per file.
// Optional prev/next buttons target this element with commandfor + command="--prev" / "--next".
class SMarquee extends HTMLElement {
  #animation = null;
  #ac = null;
  #mql = null;
  #nudgeRaf = 0;

  connectedCallback() {
    this.#ac = new AbortController();
    this.#mql = window.matchMedia("(prefers-reduced-motion: reduce)");
    this.#mql.addEventListener("change", () => this.#setup(), { signal: this.#ac.signal });

    this.addEventListener("mouseenter", () => this.#animation?.pause(), { signal: this.#ac.signal });
    this.addEventListener("mouseleave", () => this.#animation?.play(), { signal: this.#ac.signal });

    // Swipe on touch screens: a sideways swipe moves the strip like the arrows (vertical scrolling is untouched).
    let touchX = null;
    let touchY = null;
    this.addEventListener("touchstart", (e) => { touchX = e.touches[0].clientX; touchY = e.touches[0].clientY; }, { passive: true, signal: this.#ac.signal });
    this.addEventListener("touchend", (e) => {
      if (touchX === null) return;
      const dx = e.changedTouches[0].clientX - touchX;
      const dy = e.changedTouches[0].clientY - touchY;
      touchX = null;
      if (Math.abs(dx) > 30 && Math.abs(dx) > Math.abs(dy)) this.#nudge(dx < 0 ? 1 : -1);
    }, { passive: true, signal: this.#ac.signal });

    // Prev/next let shoppers browse brands without waiting for the scroll.
    this.addEventListener(
      "command",
      (event) => {
        if (event.command === "--next") this.#nudge(1);
        if (event.command === "--prev") this.#nudge(-1);
      },
      { signal: this.#ac.signal },
    );

    this.#setup();
  }

  disconnectedCallback() {
    this.#ac?.abort();
    cancelAnimationFrame(this.#nudgeRaf);
    this.#animation?.cancel();
  }

  // Shift the strip by about a third of its visible width, easing so the jump stays smooth.
  #nudge(dir) {
    const step = Math.max(160, this.clientWidth / 3);

    // Reduced motion: no animation exists, so scroll the overflow container instead.
    if (!this.#animation) {
      this.scrollBy({ left: dir * step });
      return;
    }

    const period = this.#animation.effect.getComputedTiming().duration;
    const speed = parseFloat(this.dataset.speed) || 40;
    const reverse = this.dataset.direction === "right";
    const delta = ((dir * step) / speed) * 1000 * (reverse ? -1 : 1);
    const start = this.#animation.currentTime || 0;

    this.#animation.pause();
    cancelAnimationFrame(this.#nudgeRaf);

    const t0 = performance.now();
    const ease = (t) => 1 - Math.pow(1 - t, 3);
    const tick = (now) => {
      const p = Math.min(1, (now - t0) / 350);
      this.#animation.currentTime = (((start + delta * ease(p)) % period) + period) % period;
      if (p < 1) {
        this.#nudgeRaf = requestAnimationFrame(tick);
      } else if (!this.matches(":hover")) {
        this.#animation.play();
      }
    };
    this.#nudgeRaf = requestAnimationFrame(tick);
  }

  #setup() {
    this.#animation?.cancel();
    this.#animation = null;

    const track = this.querySelector("[data-marquee-track]");
    // Reduced motion keeps the strip still but scrollable, so the arrows still work.
    this.style.overflowX = this.#mql.matches ? "auto" : "";
    if (!track || this.#mql.matches) return;

    // Ensure exactly two copies of the content so the loop is seamless.
    if (!track.dataset.cloned) {
      // Repeats: hidden from assistive tech and the tab order, but clickable.
      track.append(
        ...[...track.children].map((n) => {
          const c = n.cloneNode(true);
          c.setAttribute("aria-hidden", "true");
          // Still clickable (most visible logos are repeats); only kept out of the keyboard tab order.
          c.querySelectorAll("a, button").forEach((el) => { el.tabIndex = -1; });
          return c;
        }),
      );
      track.dataset.cloned = "true";
    }

    const distance = track.scrollWidth / 2;
    // Hidden when set up (e.g. the Black Friday Day view switches on after load): retry once it has a size.
    if (distance <= 0) {
      if (!this.retryObserver) {
        this.retryObserver = new ResizeObserver(() => {
          if (this.clientWidth > 0) {
            this.retryObserver.disconnect();
            this.retryObserver = null;
            this.#setup();
          }
        });
        this.retryObserver.observe(this);
      }
      return;
    }

    const speed = parseFloat(this.dataset.speed) || 40;
    const durationMs = (distance / speed) * 1000;
    const reverse = this.dataset.direction === "right";
    const from = reverse ? -distance : 0;
    const to = reverse ? 0 : -distance;

    this.#animation = track.animate(
      [{ transform: `translateX(${from}px)` }, { transform: `translateX(${to}px)` }],
      { duration: durationMs, iterations: Infinity, easing: "linear" },
    );
  }
}

if (!customElements.get("s-marquee")) {
  customElements.define("s-marquee", SMarquee);
}
